import styled, { useTheme } from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGiftFlow } from '../../../components/GiftFlow';
import { ParagraphLargeText } from '../../../components/Texts';
import Steps from '../../../components/Steps';
import WidthContainer from '../../../components/WidthContainer';
import { hapticsImpactLight } from '../../../common/haptic';
import ChevronLeftIcon from '../../../assets/icons/chevron-left.svg?react';
import { useGiftForm } from '../../../components/GiftFormContext';
import { useEffect, useMemo, useRef } from 'react';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import { EVENT_GIFT_FLOW_STARTED, sendEvent } from '../../../common/analytics';
import { debounce } from 'lodash';

const TitleAndStepsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const Container = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 0;

  @media ${PHONE_MEDIA_QUERY} {
    max-width: 100%;
  }
`;

const TitleHeader = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.text.title};
  cursor: pointer;
  font-size: 14px;
  padding: 8px;
  position: absolute;
  left: 0;
  &:hover {
    opacity: 0.7;
  }
`;

const TitleText = styled(ParagraphLargeText)`
  text-align: center;
  width: 100%;
`;

const CreateGiftFlow = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setValue, targetTokenAddress, network: formNetwork } = useGiftForm();

  // Get step from URL or calculate based on token in URL params only (not form state)
  const urlStep = useMemo(() => {
    const stepParam = searchParams.get('step');
    if (stepParam) {
      const parsed = parseInt(stepParam, 10);
      return isNaN(parsed) || parsed < 0 ? 0 : parsed;
    }
    // If no step in URL, check if token is in URL params - if so, skip to step 1
    // Don't check form state - we only want to skip on initial load with URL token
    const tokenAddress = searchParams.get('tokenAddress');
    const networkParam = searchParams.get('network');
    return tokenAddress && networkParam ? 1 : 0;
  }, [searchParams]);

  const {
    currentStep,
    totalSteps,
    currentTitle,
    Component,
    handleStepChange,
    onBack,
    shouldShowBackButton,
    buttonLabel,
  } = useGiftFlow(urlStep);

  // Track if we're updating URL from user action
  const isUpdatingUrlRef = useRef(false);
  const previousStepRef = useRef(currentStep);

  // Update URL when validation redirects to step 0 (missing token)
  useEffect(() => {
    // If we were on a step > 0 and now we're on step 0, validation redirected us
    // Update URL to match to break the loop
    if (previousStepRef.current > 0 && currentStep === 0 && urlStep > 0) {
      isUpdatingUrlRef.current = true;
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('step');
      setSearchParams(newParams, { replace: true });
    }
    previousStepRef.current = currentStep;
  }, [currentStep, urlStep, searchParams, setSearchParams]);

  // Sync hook when URL changes externally (browser back button)
  useEffect(() => {
    if (isUpdatingUrlRef.current) {
      isUpdatingUrlRef.current = false;
      return;
    }
    if (urlStep !== currentStep) {
      handleStepChange(urlStep);
    }
  }, [urlStep, currentStep, handleStepChange]);

  const debouncedSendEvent = useRef(
    debounce(() => {
      sendEvent(EVENT_GIFT_FLOW_STARTED);
    }, 300),
  ).current;

  useEffect(() => {
    debouncedSendEvent();
    return () => {
      debouncedSendEvent.cancel();
    };
  }, [debouncedSendEvent]);

  // Set form values from URL params if provided and form doesn't already have them
  useEffect(() => {
    const tokenAddress = searchParams.get('tokenAddress');
    const network = searchParams.get('network');

    // Only set values if URL has them and form doesn't already have them
    if (tokenAddress && network && (!targetTokenAddress || !formNetwork)) {
      setValue('targetTokenAddress', tokenAddress);
      setValue('network', network);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams, not setValue (it's stable)

  // Update URL when step changes
  const updateStepInUrl = (newStep) => {
    isUpdatingUrlRef.current = true;
    const newParams = new URLSearchParams(searchParams);
    if (newStep === 0) {
      newParams.delete('step');
    } else {
      newParams.set('step', newStep.toString());
    }
    setSearchParams(newParams, { replace: false });
  };

  const handleBack = () => {
    hapticsImpactLight();
    if (currentStep === 0) {
      navigate(-1);
    } else {
      const newStep = currentStep - 1;
      onBack(); // Use hook's method
      updateStepInUrl(newStep);
    }
  };

  const handleContinue = () => {
    const newStep = currentStep + 1;
    handleStepChange(newStep); // Use hook's method
    updateStepInUrl(newStep);
  };

  return (
    <WidthContainer>
      <Container>
        <TitleAndStepsContainer>
          <TitleHeader>
            {shouldShowBackButton && (
              <BackButton onClick={handleBack}>
                <ChevronLeftIcon width={15} height={15} />
                <span>Back</span>
              </BackButton>
            )}
            <TitleText style={{ color: theme.text.title }}>
              {currentTitle}
            </TitleText>
          </TitleHeader>
          <Steps steps={totalSteps} currentStep={currentStep} />
        </TitleAndStepsContainer>
        <Component
          isModal={false}
          isBack={previousStepRef.current > currentStep}
          currentStep={currentStep}
          onContinue={handleContinue}
          buttonLabel={buttonLabel}
        />
      </Container>
    </WidthContainer>
  );
};

export default CreateGiftFlow;
